// Layer 4 (UI) — the P&L tab, per the locked comp (variant A, picked 25 Sep):
// the GM landing's Budget → actual EBITDA bridge cut to the period, then the
// five cost categories split Fixed and Variable against Budget. The category
// totals are ranked once, in the band above the tabs, so this grid does not
// state them a second time. Presentational only.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { CARD_TABLE, DowntimeCard, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { EbitdaWaterfall } from '@/versions/v1/shared/EbitdaWaterfall';
import { costBridgeMeta } from './CostBand.transform';
import { ZERO_DELTA, costPL, formatCostPLCell, formatCostPLDelta, type CostPLCell, type CostPLRow } from './CostPLTable.transform';
import type { CostPeriod } from './Scene.transform';

type DeltaTone = 'ok' | 'warn' | 'bad' | 'neutral';

/** A Δ cell prints the money that moved, in the tone the *row* carries:
 *  `warn` when a category is over its Budget, `bad` on the Total cost row
 *  (the same overspend read at the level the month is decided at), `ok` when
 *  under. The volume-driven reagent variance is neutral ink: it is extra
 *  tonnage at the budget rate, not a miss. A Δ of exactly zero prints a muted,
 *  untoned `$0`: the money moved by nothing. */
function DeltaCell({ delta, tone }: { delta: number; tone: DeltaTone }) {
  if (delta === 0) return <TD className="whitespace-nowrap text-right text-muted">{ZERO_DELTA}</TD>;
  return (
    <TD
      className={cn('whitespace-nowrap text-right font-semibold', tone === 'neutral' && 'text-ink-primary')}
      style={tone === 'neutral' ? undefined : { color: vizToneColor(tone) }}
    >
      {formatCostPLDelta(delta)}
    </TD>
  );
}

function rowTone(row: CostPLRow, reagentVolumeDriven: boolean): DeltaTone {
  if (reagentVolumeDriven && row.key === 'Reagents') return 'neutral';
  if (row.total.delta <= 0) return 'ok';
  return row.isTotal ? 'bad' : 'warn';
}

const GROUP_EDGE = 'border-l border-[var(--hairline)]';

function Triplet({ cell, tone, strong }: { cell: CostPLCell; tone: DeltaTone; strong: boolean }) {
  return (
    <>
      <TD className={cn('whitespace-nowrap text-right text-muted', GROUP_EDGE, strong && 'font-semibold')}>
        {formatCostPLCell(cell.budget)}
      </TD>
      <TD className={cn('whitespace-nowrap text-right text-ink-primary', strong && 'font-semibold')}>
        {formatCostPLCell(cell.actual)}
      </TD>
      <DeltaCell delta={cell.delta} tone={tone} />
    </>
  );
}

function PLGrid({ period }: { period: CostPeriod }) {
  const view = costPL(period);
  const reagentVolumeDriven = view.reagentVarianceVolumeDriven;
  const row = (r: CostPLRow) => {
    const tone = rowTone(r, reagentVolumeDriven);
    return (
      <TR key={r.key} className={r.isTotal ? 'border-t-2 border-[var(--hairline-strong)]' : undefined}>
        <TD className="whitespace-nowrap font-semibold text-ink-primary">{r.label}</TD>
        <Triplet cell={r.fixed} tone={tone} strong={r.isTotal} />
        <Triplet cell={r.variable} tone={tone} strong={r.isTotal} />
      </TR>
    );
  };

  return (
    <Commentable name="CostPL">
      <div>
        <DowntimeCard title="P&L by cost category" actions={SYN_CHIP}>
          <div className={CARD_TABLE}>
            <Table wrap={false} className="tabular-nums" aria-label="P&L by cost category">
              <THead>
                <TR>
                  <TH rowSpan={2} className="align-bottom">Category</TH>
                  <TH colSpan={3} className={cn('text-center', GROUP_EDGE)}>Fixed</TH>
                  <TH colSpan={3} className={cn('text-center', GROUP_EDGE)}>Variable</TH>
                </TR>
                <TR>
                  {(['Fixed', 'Variable'] as const).flatMap((group) => [
                    <TH key={`${group}-b`} className={cn('text-right', GROUP_EDGE)}>Budget</TH>,
                    <TH key={`${group}-a`} className="text-right">Actual</TH>,
                    <TH key={`${group}-d`} className="text-right">Δ</TH>,
                  ])}
                </TR>
              </THead>
              <TBody>
                {view.rows.map(row)}
                {row(view.total)}
              </TBody>
            </Table>
          </div>
          <p className="mt-2 text-xs text-muted">
            Fixed = quantity independent of tonnes milled · Variable = quantity scales with tonnes milled
          </p>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

export function CostPLTable({ period }: { period: CostPeriod }) {
  return (
    <div className="space-y-3">
      <DowntimeCard title="EBITDA bridge — Budget to actual" meta={costBridgeMeta(period)}>
        <div data-cost-bridge>
          <EbitdaWaterfall period={period} />
        </div>
      </DowntimeCard>
      <PLGrid period={period} />
    </div>
  );
}
